import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getSeoForPath, SITE_NAME, SITE_URL, FAVICON_URL, type SeoMeta } from './seoData';
import { batch1 } from '@/pages/article/pillarData';
import { batch2 } from '@/pages/article/pillarData2';

const pillarArticles = [...batch1, ...batch2];

function orgJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: FAVICON_URL,
  };
}

function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
  };
}

function articleJsonLd(meta: SeoMeta) {
  const url = `${SITE_URL}${meta.path}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: meta.title,
    description: meta.description,
    image: meta.image ? [meta.image] : undefined,
    datePublished: meta.published || '2026-08-01',
    dateModified: '2026-08-26',
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: FAVICON_URL },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  };
}

function breadcrumbJsonLd(meta: SeoMeta) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: meta.title, item: `${SITE_URL}${meta.path}` },
    ],
  };
}

function faqJsonLd(faq: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

export default function JsonLd() {
  const { pathname } = useLocation();
  const meta = getSeoForPath(pathname);

  useEffect(() => {
    document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());

    const blocks: Record<string, unknown>[] = [];
    if (meta.path === '/') {
      blocks.push(orgJsonLd(), websiteJsonLd());
    } else {
      blocks.push(articleJsonLd(meta), breadcrumbJsonLd(meta));
      const pillar = pillarArticles.find((a) => a.slug === meta.path);
      if (pillar && pillar.faq.length > 0) {
        blocks.push(faqJsonLd(pillar.faq));
      }
    }

    blocks.forEach((obj) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(obj);
      document.head.appendChild(script);
    });
  }, [meta]);

  return null;
}